/* ═══════════════════════════════════════════════════
   BUNDLE BUILDER — Cart Drawer Progress Bar JS
   Shows/hides a tier-based progress bar in the cart
   drawer based on eligible products in cart.

   NOTE — Discount application:
   Discounts are handled by a Shopify Discount Function
   (Shopify Functions API) that reads the _bundle_item
   and _bundle_id line-item properties set by
   bundle-builder-page.js. The Function groups items by
   _bundle_id, counts total quantity, and applies the
   correct tier percentage at checkout.
   No client-side discount code redirect needed here.
═══════════════════════════════════════════════════ */
(function initBundleBuilderCart() {
    'use strict';
  
    var bbBlock = null;

    var productsMutationObserver = null;
    var bundleLineKeysById = {};
    var savedCardKeys = {};
    var removeBundleInFlightById = {};
    var bundleSlicesById = {};
    var subToggleInFlightById = {};
    var savedSellingPlansByBundle = {}; // { bundleGroupId: { handle: planId } }

    /* ── Cart-updating cloak ──────────────────────────────────────────────────
       We set a class on <body> rather than on #CartDrawer-CartItems directly.
       Dawn's refreshSections() replaces #CartDrawer-CartItems with fresh HTML,
       discarding any class on the old node. A body-level CSS rule targets the
       NEW element by ID automatically, so items stay hidden until groupBundleItems
       finishes re-grouping them. The 3 s safety timer is the backstop if anything
       goes wrong before uncloak() fires.
    ──────────────────────────────────────────────────────────────────────────── */
    var bbUncloakTimer = null;
    function setCartUpdating() {
      document.body.classList.add('bb-cart-updating');
      clearTimeout(bbUncloakTimer);
      bbUncloakTimer = setTimeout(clearCartUpdating, 3000);
    }
    function clearCartUpdating() {
      clearTimeout(bbUncloakTimer);
      bbUncloakTimer = null;
      document.body.classList.remove('bb-cart-updating');
    }
  
    /* ─────────────────────────────────────────────────
       ELIGIBLE PRODUCTS + TIERS — filled by parseTierConfigFromDom()
    ───────────────────────────────────────────────── */
    var eligibleIds = [];
    var eligibleHandles = [];
    var hasConfig = false;

    function parseTierConfigFromDom() {
      bbBlock = document.getElementById('bbCartUpsell');
      eligibleIds = [];
      var rid = ((bbBlock && bbBlock.getAttribute('data-eligible-product-ids')) || '').trim();
      if (rid) {
        eligibleIds = rid.split(',').map(function(id) { return id.trim(); }).filter(function(id) { return id && id !== '0'; });
      }
      eligibleHandles = [];
      var rh = ((bbBlock && bbBlock.getAttribute('data-eligible-product-handles')) || '').trim();
      if (rh) {
        eligibleHandles = rh.split(',').map(function(h) { return h.trim(); }).filter(Boolean);
      }
      hasConfig = eligibleIds.length > 0 || eligibleHandles.length > 0;
      tiers = [];
      try {
        var rawTiers = JSON.parse((bbBlock && bbBlock.getAttribute('data-tiers')) || '[]');
        // pct must match each bb_tier block’s "Discount %" in Theme → Bundle Builder — Cart
        // (see snippets/cart-drawer.liquid data-tiers). Stale storefront theme can show old % until push.
        tiers = rawTiers
          .map(function(t) {
            return {
              qty: Number(t.qty) || 0,
              pct: Number(t.pct) || 0,
              label: t.label != null ? String(t.label) : ''
            };
          })
          .filter(function(t) { return t.qty > 0; })
          .sort(function(a, b) { return a.qty - b.qty; });
      } catch (e) { tiers = []; }
      if (!tiers.length) {
        tiers = [
          { qty: 2, pct: 15, label: '2 products – 15% off' },
          { qty: 3, pct: 20, label: '3 products – 20% off' },
          { qty: 4, pct: 25, label: '4 products – 25% off' }
        ];
      }
    }

    function getMaxQty() {
      return tiers.length ? tiers[tiers.length - 1].qty : 4;
    }

    function rehydrateBundleBuilderCart() {
      parseTierConfigFromDom();
      attachProductsObserver();
      fetchCartAndSync();
    }

    if (typeof window !== 'undefined') {
      window.bbRehydrateBundleBuilderCart = rehydrateBundleBuilderCart;
    }

    function getProductsContainer() {
      // Dawn: rows live under #CartDrawer-CartItems. The sibling [data-products] div is often empty
      // but can gain wrapper nodes (animations) — if we treated that as the container, findCartRow
      // would miss every line → missingNodes → empty bundle card after removing another line.
      var cartDrawerRoot = document.querySelector('#CartDrawer-CartItems');
      if (cartDrawerRoot) return cartDrawerRoot;

      var dataProducts = document.querySelector('[data-products]');
      if (!dataProducts || !dataProducts.children || !dataProducts.children.length) {
        return dataProducts || null;
      }
      return dataProducts;
    }

    function findCartRow(root, key) {
      if (!root || !key) return null;
      var k = String(key);
      if (typeof CSS !== 'undefined' && CSS.escape) {
        k = CSS.escape(k);
      }
      return root.querySelector('.cart__item[data-key="' + k + '"], .cart-item[data-key="' + k + '"]');
    }

    function getHiddenRows(root) {
      if (!root) return [];
      return root.querySelectorAll('.cart__item[data-bb-hidden], .cart-item[data-bb-hidden]');
    }

    /** Dawn drawer: lines are `tr.cart-item` inside `table.cart-items tbody` under `#CartDrawer-CartItems`. */
    function getLineItemsRoot(productsContainer) {
      if (!productsContainer) return null;
      var tbody = productsContainer.querySelector('table.cart-items tbody');
      if (tbody) return tbody;
      return productsContainer.querySelector('.cart__items') || productsContainer;
    }

    /**
     * After /cart/update.js, Shopify does not return section HTML. Use cart-drawer.refreshSections()
     * (same network path renderOrRefresh falls back to) to re-fetch cart-drawer + icon markup, then
     * re-run bundle grouping. renderContents() is for responses that already include sections (e.g. /cart/add.js).
     */
    function refreshCartDrawerSectionsThenSync(options) {
      var openDrawer = options && options.openDrawer === true;
      var cd = document.querySelector('cart-drawer');
      if (cd && typeof cd.refreshSections === 'function') {
        return cd.refreshSections({ openDrawer: openDrawer }).then(function () {
          parseTierConfigFromDom();
          attachProductsObserver();
          fetchCartAndSync();
        });
      }
      parseTierConfigFromDom();
      attachProductsObserver();
      fetchCartAndSync();
      return Promise.resolve();
    }

    function attachProductsObserver() {
      if (productsMutationObserver) {
        try { productsMutationObserver.disconnect(); } catch (e) {}
        productsMutationObserver = null;
      }
      var productsEl = getProductsContainer();
      if (!productsEl) return;
      productsMutationObserver = new MutationObserver(function (mutations) {
        if (isGroupingItems) return;
        var isOwnMutation = mutations.every(function (m) {
          if (m.type === 'attributes') {
            return m.target.hasAttribute('data-bb-hidden') || m.attributeName === 'data-bb-hidden';
          }
          return false;
        });
        if (isOwnMutation) return;
        // Do NOT apply bb-cloaked here. External scripts (e.g. BOGOS free-gift app)
        // legitimately mutate #CartDrawer-CartItems. Before the CSS fix this class had
        // no visual effect; now it correctly hides the container — but we have no
        // controlled uncloak path for externally-triggered syncs, which could leave
        // the cart invisible for up to the safety-timer duration. Cloaking is only
        // safe in the controlled remove / fast-path flows below.
        fetchCartAndSync();
      });
      productsMutationObserver.observe(productsEl, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-bb-hidden'] });
    }

    function getBundleGroupId(item) {
      if (!item || !item.properties) return '__legacy__';
      var bid = item.properties['_bundle_id'];
      if (bid == null || bid === '') return '__legacy__';
      return String(bid);
    }
  
    function isEligible(item) {
      if (eligibleIds.length && eligibleIds.some(function(id) { return String(item.product_id) === id; })) return true;
      if (eligibleHandles.length && item.handle && eligibleHandles.some(function(h) { return item.handle === h; })) return true;
      if (!hasConfig) return true;
      return false;
    }
  
    /* A cart item only counts as part of the bundle if it was explicitly added
       through the bundle builder page — identified by the _bundle_item line
       property set by bundle-builder-page.js.
       Products that happen to be "eligible" but were added from other pages
       (PDP, recommendations, etc.) must NOT be counted or grouped. */
    function isBundleItem(item) {
      if (!item.properties) return false;
      return item.properties['_bundle_item'] === 'true' || item.properties['_bundle_item'] === true;
    }
  
    /* ─────────────────────────────────────────────────
       TIERS (re-read from #bbCartUpsell after drawer section refresh)
    ───────────────────────────────────────────────── */
    var tiers = [];
    parseTierConfigFromDom();
  
    function getActiveTier(qty) {
      var active = null;
      for (var i = tiers.length - 1; i >= 0; i--) {
        if (qty >= tiers[i].qty) { active = tiers[i]; break; }
      }
      return active;
    }
  
    /* ─────────────────────────────────────────────────
       PROGRESS BAR
    ───────────────────────────────────────────────── */
    function updateProgressBar(eligibleQty) {
      if (!bbBlock) return;
      var fillBar    = bbBlock.querySelector('[data-bb-fill]');
      var nextGrid   = bbBlock.querySelector('[data-bb-next-grid]');
      var nextText   = bbBlock.querySelector('[data-bb-next-text]');
      var unlockedEl = bbBlock.querySelector('[data-bb-unlocked-text]');
      var counterEl  = bbBlock.querySelector('[data-bb-counter]');
      var tierLabels = bbBlock.querySelectorAll('[data-bb-tier-label]');
  
      var maxQ = getMaxQty();
      var fillPct = Math.min(Math.round((eligibleQty / maxQ) * 100), 100);
      var clipPct = 100 - fillPct;
  
      if (fillBar)  fillBar.style.width = fillPct + '%';
      if (nextGrid) nextGrid.style.clipPath = 'inset(0px ' + clipPct + '% 0px 0px)';
      if (counterEl) counterEl.textContent = '(' + eligibleQty + '/' + maxQ + ')';
  
      var unlockedTier = getActiveTier(eligibleQty);
      if (unlockedEl) {
        unlockedEl.textContent = unlockedTier ? unlockedTier.pct + '% off unlocked!' : '';
      }
  
      var nextTier = null;
      for (var j = 0; j < tiers.length; j++) {
        if (tiers[j].qty > eligibleQty) { nextTier = tiers[j]; break; }
      }
      if (nextText) {
        if (nextTier) {
          var need = nextTier.qty - eligibleQty;
          nextText.textContent = 'Add ' + need + ' more for ' + nextTier.pct + '% off';
        } else {
          nextText.textContent = 'Max discount unlocked! 🎉';
        }
      }
  
      tierLabels.forEach(function(lbl) {
        var tierQty = parseInt(lbl.getAttribute('data-tier-qty') || '0', 10);
        lbl.classList.toggle('is-active', unlockedTier !== null && tierQty <= eligibleQty);
      });
    }
  
    /* ─────────────────────────────────────────────────
       BUNDLE GROUP CARD
       The card lives in #bb-group-card-anchor — before [data-products].
       Dawn’s cart-change replaces all of .drawer__inner; anchor + rows are re-rendered.
  
       groupBundleItems:
       - Reads bundle items from .cart__items (DOM)
       - If card content unchanged → just re-hides source rows
       - If changed → patches card text + carousel in-place
       - If no bundle items → removes the card
       - Source .cart__item rows are hidden with display:none
    ───────────────────────────────────────────────── */
  
    /* Keys (cart line item keys) of all bundle items — used to cloak during drawer re-renders. */
    var bundleLineKeys = [];
  
    function findGroupCard(bundleGroupId) {
      var anchor = document.getElementById('bb-group-card-anchor');
      if (!anchor || !bundleGroupId) return null;
      var cards = anchor.querySelectorAll('.bb-group-card');
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute('data-bb-bundle-id') === bundleGroupId) return cards[i];
      }
      return null;
    }
  
    /* ── Remove entire bundle group (one _bundle_id) from cart ── */
    function removeBundleFor(bundleGroupId) {
      if (!bundleGroupId || removeBundleInFlightById[bundleGroupId]) return;
      var keysToRemove = (bundleLineKeysById[bundleGroupId] || []).slice();
      if (!keysToRemove.length) return;
      removeBundleInFlightById[bundleGroupId] = true;
  
      var updates = {};
      keysToRemove.forEach(function(k) { updates[k] = 0; });
  
      var card = findGroupCard(bundleGroupId);
      var btn = card && card.querySelector('.bb-group-card__remove-btn');
      if (btn) { btn.disabled = true; btn.style.opacity = '0.4'; }

      setCartUpdating();
  
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: updates })
      })
      .then(function(r) {
        if (!r.ok) throw new Error('update failed');
        return r.json();
      })
      .then(function() {
        return refreshCartDrawerSectionsThenSync({ openDrawer: false });
      })
      .then(function() {
        removeBundleInFlightById[bundleGroupId] = false;
        savedCardKeys = {};
      })
      .catch(function() {
        removeBundleInFlightById[bundleGroupId] = false;
        if (btn) { btn.disabled = false; btn.style.opacity = ''; }
        clearCartUpdating();
        fetchCartAndSync();
      });
    }
  
    /* ── Remove a single bundle line item from cart ── */
    var removeLineItemInFlight = false;
  
    function removeLineItem(key) {
      if (!key || removeLineItemInFlight) return;
      removeLineItemInFlight = true;

      var btn = document.querySelector('.bb-group-card__slide-remove[data-key="' + key + '"]');
      if (btn) { btn.disabled = true; btn.style.opacity = '0.4'; }

      setCartUpdating();

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: { [key]: 0 } })
      })
        .then(function(r) {
          if (!r.ok) throw new Error('update failed');
          return r.json();
        })
        .then(function() {
          return refreshCartDrawerSectionsThenSync({ openDrawer: false });
        })
        .then(function() {
          removeLineItemInFlight = false;
          bundleLineKeys = bundleLineKeys.filter(function(k) { return k !== key; });
          savedCardKeys = {};
        })
        .catch(function() {
          removeLineItemInFlight = false;
          if (btn) { btn.disabled = false; btn.style.opacity = ''; }
          clearCartUpdating();
          fetchCartAndSync();
        });
    }
  
    function buildSlidesHTML(bundleItems) {
      var slides = [];
      bundleItems.forEach(function(item) {
        var imgHTML = item.imgSrc
          ? '<img src="' + item.imgSrc + '" alt="' + item.title.replace(/"/g, '&quot;') + '" width="72" height="72" loading="lazy">'
          : '<div class="bb-group-card__img-placeholder"></div>';
        var slideHTML = (
          '<div class="bb-group-card__slide">' +
            '<div class="bb-group-card__img-wrap">' +
              imgHTML +
              '<button type="button" class="bb-group-card__slide-remove" data-key="' + item.key + '" aria-label="Remove ' + item.title.replace(/"/g, '&quot;') + '">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>' +
              '</button>' +
            '</div>' +
            '<span class="bb-group-card__name">' + item.title + '</span>' +
          '</div>'
        );
        // Expand duplicates: qty=2 → 2 individual slides, each with the same key
        for (var i = 0; i < item.qty; i++) {
          slides.push(slideHTML);
        }
      });
      return slides.join('');
    }
  
    function bindSlideRemoveButtons(carousel) {
      carousel.querySelectorAll('.bb-group-card__slide-remove').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.stopPropagation();
          var key = btn.getAttribute('data-key');
          removeLineItem(key);
        });
      });
    }

    function formatMoneyCents(cents) {
      var value = Number(cents) || 0;
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
        return Shopify.formatMoney(value, fmt);
      }
      return '$' + (value / 100).toFixed(2);
    }
  
    /* ─────────────────────────────────────────────────
       SUBSCRIPTION TOGGLE
    ───────────────────────────────────────────────── */

    function fetchSellingPlanIdForHandle(handle) {
      return fetch('/products/' + handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var product = data && data.product;
          if (!product || !product.selling_plan_groups || !product.selling_plan_groups.length) return null;
          // Prefer SKIO groups, fall back to any group
          var skioGroups = product.selling_plan_groups.filter(function(g) { return g.app_id === 'SKIO'; });
          var groups = skioGroups.length ? skioGroups : product.selling_plan_groups;
          // Try plans with "Delivery" in name first, then fall back to first available plan
          for (var i = 0; i < groups.length; i++) {
            var plans = groups[i].selling_plans || [];
            for (var j = 0; j < plans.length; j++) {
              if ((plans[j].name || '').indexOf('Delivery') !== -1) return plans[j].id;
            }
          }
          for (var i = 0; i < groups.length; i++) {
            if (groups[i].selling_plans && groups[i].selling_plans.length) return groups[i].selling_plans[0].id;
          }
          return null;
        })
        .catch(function() { return null; });
    }

    function buildPlanMapFromSlice(bundleSlice) {
      var planMap = {};
      bundleSlice.forEach(function(item) {
        if (item.handle && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          planMap[item.handle] = item.selling_plan_allocation.selling_plan.id;
        }
      });
      return planMap;
    }

    function toggleSubscriptionForBundle(bundleGroupId, bundleSlice, hasSubscription, toggleBtn) {
      if (subToggleInFlightById[bundleGroupId]) return;
      subToggleInFlightById[bundleGroupId] = true;
      if (toggleBtn) toggleBtn.disabled = true;

      var card = findGroupCard(bundleGroupId);
      if (card) card.classList.add('bb-group-card--loading');
      // setCartUpdating() is called AFTER API calls complete, not here, so the spinner
      // stays visible during the API phase and the anchor fade only starts at re-render time.

      function cartChange(key, qty, plan) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity: qty, selling_plan: plan })
        }).then(function(r) { return r.json(); });
      }

      var chain;
      if (hasSubscription) {
        // Save selling plan IDs before removing so we can restore them later
        var planMapToSave = buildPlanMapFromSlice(bundleSlice);
        if (Object.keys(planMapToSave).length) savedSellingPlansByBundle[bundleGroupId] = planMapToSave;

        // Sequential — Shopify's cart is a single shared resource; parallel calls race
        // and can silently drop updates, leaving some items still subscribed.
        chain = bundleSlice.reduce(function(p, item) {
          return p.then(function() { return cartChange(item.key, item.quantity, null); });
        }, Promise.resolve());
      } else {
        // Priority order for finding the selling plan ID per item:
        //   1. _selling_plan_id line property (set by bundle-builder-page.js at add-to-cart time — most reliable)
        //   2. savedSellingPlansByBundle (populated when subscription was previously active this session)
        //   3. /products/{handle}.json fetch (last resort for older cart items without the property)
        var savedPlans = savedSellingPlansByBundle[bundleGroupId] || {};

        var propPlanMap = {};
        bundleSlice.forEach(function(item) {
          var pid = item.properties && item.properties['_selling_plan_id'];
          if (item.handle && pid) propPlanMap[item.handle] = parseInt(pid, 10);
        });

        var missingHandles = [];
        bundleSlice.forEach(function(item) {
          if (!item.handle) return;
          if (propPlanMap[item.handle] || savedPlans[item.handle]) return;
          if (missingHandles.indexOf(item.handle) === -1) missingHandles.push(item.handle);
        });

        var planResolve = missingHandles.length
          ? Promise.all(missingHandles.map(function(h) {
              return fetchSellingPlanIdForHandle(h).then(function(planId) { return { handle: h, planId: planId }; });
            })).then(function(results) {
              var map = {};
              results.forEach(function(r) { if (r.planId) map[r.handle] = r.planId; });
              return map;
            })
          : Promise.resolve({});

        // Sequential for the same reason — avoid parallel cart write races
        chain = planResolve.then(function(fetchedMap) {
          return bundleSlice.reduce(function(p, item) {
            return p.then(function() {
              var planId = propPlanMap[item.handle] || savedPlans[item.handle] || fetchedMap[item.handle];
              if (!planId) return Promise.resolve();
              return cartChange(item.key, item.quantity, planId);
            });
          }, Promise.resolve());
        });
      }

      chain
        .then(function() {
          // API calls done — remove spinner, then start the re-render phase.
          // setCartUpdating() fades the anchor out right as the section refresh begins,
          // so there is no visible rebuild flicker. uncloak() fades it back in.
          subToggleInFlightById[bundleGroupId] = false;
          if (card) card.classList.remove('bb-group-card--loading');
          setCartUpdating();
          savedCardKeys = {};
          return refreshCartDrawerSectionsThenSync({ openDrawer: false });
        })
        .catch(function() {
          subToggleInFlightById[bundleGroupId] = false;
          if (card) card.classList.remove('bb-group-card--loading');
          clearCartUpdating();
          if (toggleBtn) toggleBtn.disabled = false;
          fetchCartAndSync();
        });
    }

    function groupBundleItems(bundleCartItems) {
      var anchor = document.getElementById('bb-group-card-anchor');
      var productsContainer = getProductsContainer();

      if (!anchor || !productsContainer) return;
  
      var itemsContainer = getLineItemsRoot(productsContainer);

      function uncloak() {
        clearCartUpdating();
      }
  
      isGroupingItems = true;
  
      getHiddenRows(itemsContainer).forEach(function(el) {
        el.removeAttribute('data-bb-hidden');
        el.style.display = '';
      });
  
      if (!bundleCartItems || !bundleCartItems.length) {
        anchor.querySelectorAll('.bb-group-card').forEach(function(c) { c.remove(); });
        savedCardKeys = {};
        bundleGroupMissRetries = 0;
        uncloak();
        requestAnimationFrame(function() { requestAnimationFrame(function() { isGroupingItems = false; }); });
        return;
      }
  
      var groups = {};
      bundleCartItems.forEach(function(apiItem) {
        var gid = getBundleGroupId(apiItem);
        if (!groups[gid]) groups[gid] = [];
        groups[gid].push(apiItem);
      });
  
      var groupIds = Object.keys(groups);
      var allRows = [];
      var missingNodes = false;
  
      for (var gi = 0; gi < groupIds.length; gi++) {
        var bundleGroupId = groupIds[gi];
        var apiItems = groups[bundleGroupId];
        var rows = [];
        for (var ai = 0; ai < apiItems.length; ai++) {
          var apiItem = apiItems[ai];
          var node = findCartRow(itemsContainer, apiItem.key);
          if (!node) missingNodes = true;
          var imgSrc = apiItem.featured_image && apiItem.featured_image.url
            ? apiItem.featured_image.url : '';
          if (!imgSrc && node) {
            var imgEl = node.querySelector('img');
            imgSrc = imgEl ? (imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '') : '';
          }
          if (imgSrc && imgSrc.indexOf('//') === 0) imgSrc = 'https:' + imgSrc;
          if (imgSrc) {
            imgSrc = imgSrc.replace(/(_\d+x\d*|_\d*x\d+)(\.[a-z]+(\?|$))/i, '$2');
            imgSrc = imgSrc.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2');
          }
          rows.push({
            node: node,
            key: apiItem.key,
            title: apiItem.product_title || apiItem.title || '',
            imgSrc: imgSrc,
            qty: apiItem.quantity
          });
        }
        allRows.push({ id: bundleGroupId, apiItems: apiItems, rows: rows });
      }
  
      if (missingNodes) {
        isGroupingItems = false;
        uncloak();
        if (bundleGroupMissRetries < 3) {
          bundleGroupMissRetries += 1;
          clearTimeout(bundleGroupRetryTimer);
          bundleGroupRetryTimer = setTimeout(function() {
            bundleGroupRetryTimer = null;
            fetchCartAndSync();
          }, 120);
        } else {
          bundleGroupMissRetries = 0;
        }
        return;
      }
      bundleGroupMissRetries = 0;
  
      anchor.querySelectorAll('.bb-group-card').forEach(function(card) {
        var cid = card.getAttribute('data-bb-bundle-id');
        if (!cid || groupIds.indexOf(cid) === -1) {
          card.remove();
          if (cid) delete savedCardKeys[cid];
        }
      });
  
      for (var g = 0; g < allRows.length; g++) {
        var grp = allRows[g];
        var bundleGroupId = grp.id;
        var bundleItems = grp.rows;
        var bundleSlice = grp.apiItems;
  
        var existingCard = findGroupCard(bundleGroupId);
        var createdNewCard = false;
        if (!existingCard) {
          createdNewCard = true;
          existingCard = document.createElement('div');
          existingCard.className = 'bb-group-card';
          existingCard.setAttribute('data-bb-bundle-id', bundleGroupId);
          existingCard.innerHTML =
            '<div class="bb-group-card__header">' +
              '<span class="bb-group-card__title"></span>' +
              '<span class="bb-group-card__tier-label"></span>' +
              '<button type="button" class="bb-group-card__sub-toggle" data-sub-toggle aria-label="Toggle subscription">' +
                '<span class="bb-group-card__sub-check"><svg class="bb-group-card__sub-checkmark" width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3L3 5L7 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
                
                'Subscribe &amp; Save' +
                '<svg class="bb-group-card__sub-icon" width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M11.5 1C11.776 1 12 1.224 12 1.5V2.5H13C14.105 2.5 15 3.395 15 4.5V13C15 14.105 14.105 15 13 15H3C1.895 15 1 14.105 1 13V4.5C1 3.395 1.895 2.5 3 2.5H4V1.5C4 1.224 4.224 1 4.5 1C4.776 1 5 1.224 5 1.5V2.5H11V1.5C11 1.224 11.224 1 11.5 1ZM2 6.5V13C2 13.552 2.448 14 3 14H13C13.552 14 14 13.552 14 13V6.5H2ZM3 3.5C2.448 3.5 2 3.948 2 4.5V5.5H14V4.5C14 3.948 13.552 3.5 13 3.5H3Z" fill="currentColor"/></svg>' +
              '</button>' +
              '<button type="button" class="bb-group-card__remove-btn" aria-label="Remove bundle" title="Remove all bundle items">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>' +
              '</button>' +
            '</div>' +
            '<div class="bb-group-card__body">' +
              '<div class="bb-group-card__carousel" data-bb-carousel></div>' +
              '<div class="bb-group-card__summary">' +
                '<span class="bb-group-card__discount-applied"></span>' +
                '<div class="bb-group-card__prices">' +
                  '<s class="bb-group-card__old-price"></s>' +
                  '<strong class="bb-group-card__final-price"></strong>' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<div class="bb-group-card__loader"><div class="bb-group-card__loader-spinner"></div></div>';
          (function(bid, card) {
            card.querySelector('.bb-group-card__remove-btn').addEventListener('click', function(e) {
              e.stopPropagation();
              removeBundleFor(bid);
            });
            card.querySelector('[data-sub-toggle]').addEventListener('click', function(e) {
              e.stopPropagation();
              var btn = card.querySelector('[data-sub-toggle]');
              toggleSubscriptionForBundle(bid, bundleSlicesById[bid] || [], btn.classList.contains('is-active'), btn);
            });
          })(bundleGroupId, existingCard);
          anchor.appendChild(existingCard);
          initDragScroll(existingCard.querySelector('[data-bb-carousel]'));
        }
  
        var totalQty = bundleItems.reduce(function(sum, it) { return sum + it.qty; }, 0);
        var activeTier = getActiveTier(totalQty);
        var tierPct = activeTier ? activeTier.pct : 0;
        var hasSubscription = bundleSlice.some(function(it) {
          return it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
        });
        var cartOriginal = bundleSlice.reduce(function(sum, it) {
          return sum + (Number(it.original_line_price) || Number(it.final_line_price) || 0);
        }, 0);
        var cartFinal = bundleSlice.reduce(function(sum, it) {
          return sum + (Number(it.final_line_price) || Number(it.original_line_price) || 0);
        }, 0);
        /* Subscription: cart already has selling-plan prices — match cart subtotal.
           One-time: bundle tier is not in /cart.js until checkout — project tier %. */
        var displayOriginal = cartOriginal;
        var displayFinal = cartFinal;
        var tierAtCheckout = false;
        if (hasSubscription && cartFinal < cartOriginal) {
          displayOriginal = cartOriginal;
          displayFinal = cartFinal;
          tierAtCheckout = tierPct > 0;
        } else if (tierPct > 0) {
          displayFinal = Math.round(cartOriginal * (100 - tierPct) / 100);
          tierAtCheckout = false;
        }
        var fingerprint =
          bundleItems.map(function(it) { return it.key + ':' + it.qty; }).join('|') +
          '@' + tierPct +
          '@p' + displayOriginal + ':' + displayFinal +
          (hasSubscription ? '+sub' : '');
  
        var carousel = existingCard.querySelector('[data-bb-carousel]');
        var carouselHasSlides = carousel && carousel.querySelector('.bb-group-card__slide');
        // After Dawn replaces .drawer__inner, in-memory savedCardKeys can still match while the new card DOM has no slides yet — repaint whenever empty or new card or fingerprint changed.
        var shouldRepaintCard = createdNewCard || savedCardKeys[bundleGroupId] !== fingerprint || !carouselHasSlides;

        if (shouldRepaintCard) {
          var titleEl2 = existingCard.querySelector('.bb-group-card__title');
          var titleBase = groupIds.length > 1 ? ('Bundle #' + (groupIds.indexOf(bundleGroupId) + 1)) : 'Bundle';
          if (titleEl2) titleEl2.textContent = titleBase + ' (' + totalQty + '/' + getMaxQty() + ' items)';
          var tierEl = existingCard.querySelector('.bb-group-card__tier-label');
          if (tierEl) tierEl.textContent = activeTier ? tierPct + '% off unlocked' : 'Bundle';
          var subToggle = existingCard.querySelector('[data-sub-toggle]');
          if (subToggle) subToggle.classList.toggle('is-active', hasSubscription);
          bundleSlicesById[bundleGroupId] = bundleSlice;
          // Keep selling plan IDs fresh — always save when subscription is active
          if (hasSubscription) {
            var freshPlanMap = {};
            bundleSlice.forEach(function(item) {
              if (item.handle && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
                freshPlanMap[item.handle] = item.selling_plan_allocation.selling_plan.id;
              }
            });
            if (Object.keys(freshPlanMap).length) savedSellingPlansByBundle[bundleGroupId] = freshPlanMap;
          }
          if (carousel) {
            carousel.innerHTML = buildSlidesHTML(bundleItems);
            bindSlideRemoveButtons(carousel);
            initDragScroll(carousel);
          }
          var discountAppliedEl = existingCard.querySelector('.bb-group-card__discount-applied');
          var oldPriceEl = existingCard.querySelector('.bb-group-card__old-price');
          var finalPriceEl = existingCard.querySelector('.bb-group-card__final-price');
          if (discountAppliedEl) {
            if (tierPct > 0) {
              discountAppliedEl.textContent = tierAtCheckout
                ? tierPct + '% OFF APPLIED'
                : tierPct + '% OFF APPLIED';
              discountAppliedEl.style.display = '';
            } else {
              discountAppliedEl.style.display = 'none';
            }
          }
          if (oldPriceEl) {
            if (displayFinal < displayOriginal) {
              oldPriceEl.textContent = formatMoneyCents(displayOriginal);
              oldPriceEl.style.display = '';
            } else {
              oldPriceEl.style.display = 'none';
            }
          }
          if (finalPriceEl) {
            finalPriceEl.textContent = formatMoneyCents(displayFinal);
          }
          savedCardKeys[bundleGroupId] = fingerprint;
        }
  
        bundleItems.forEach(function(item) {
          if (!item.node) return;
          item.node.setAttribute('data-bb-hidden', '1');
          item.node.style.display = 'none';
        });
      }
  
      uncloak();
      bundleGroupMissRetries = 0;
      requestAnimationFrame(function() { requestAnimationFrame(function() { isGroupingItems = false; }); });
    }

    function initDragScroll(el) {
      if (!el) return;
      var isDown = false;
      var startX, scrollLeftStart;
      var canDrag = false;
  
      function updateCursor() {
        canDrag = el.scrollWidth > el.clientWidth;
        el.style.cursor = canDrag ? 'grab' : 'default';
      }
      updateCursor();
      if (window.ResizeObserver) {
        new ResizeObserver(updateCursor).observe(el);
      }
  
      el.addEventListener('mousedown', function(e) {
        if (!canDrag) return;
        if (e.button !== 0) return;
        isDown = true;
        startX = e.pageX;
        scrollLeftStart = el.scrollLeft;
        el.classList.add('is-grabbing');
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        e.preventDefault();
      });
  
      document.addEventListener('mousemove', function(e) {
        if (!isDown) return;
        var dx = e.pageX - startX;
        el.scrollLeft = scrollLeftStart - dx;
      });
  
      document.addEventListener('mouseup', function() {
        if (!isDown) return;
        isDown = false;
        el.classList.remove('is-grabbing');
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
      });
    }
  
    /* ─────────────────────────────────────────────────
       SYNC
    ───────────────────────────────────────────────── */
    function updateBadgeLabels(eligibleQty) {
      var activeTier = getActiveTier(eligibleQty);
      document.querySelectorAll('[data-bb-item-badge]').forEach(function(badge) {
        var label = badge.querySelector('.bb-cart-item-badge__label');
        if (!label) return;
        label.textContent = activeTier ? 'Bundle – ' + activeTier.pct + '% off' : 'Bundle';
      });
    }
  
    function syncVisibility(cartItems) {
      if (!cartItems || !cartItems.length) {
        if (bbBlock) bbBlock.style.display = 'none';
        bundleLineKeys = [];
        bundleLineKeysById = {};
        isGroupingItems = false;
        groupBundleItems([]);
        return;
      }
  
      // Only items explicitly added through the bundle builder count
      var bundleItems = cartItems.filter(isBundleItem);
      var bundleQty   = bundleItems.reduce(function(sum, it) { return sum + it.quantity; }, 0);
  
      bundleLineKeysById = {};
      bundleItems.forEach(function(it) {
        var bid = getBundleGroupId(it);
        if (!bundleLineKeysById[bid]) bundleLineKeysById[bid] = [];
        bundleLineKeysById[bid].push(it.key);
      });
      bundleLineKeys = bundleItems.map(function(it) { return it.key; });
  
      if (bundleQty < 1) {
        if (bbBlock) bbBlock.style.display = 'none';
        updateBadgeLabels(0);
        bundleLineKeysById = {};
        isGroupingItems = false;
        groupBundleItems([]);
        return;
      }
      updateProgressBar(bundleQty);
      updateBadgeLabels(bundleQty);
      if (bbBlock) bbBlock.style.display = '';
      groupBundleItems(bundleItems);
    }
  
    /* ─────────────────────────────────────────────────
       SYNC SCHEDULING
       fetchCartAndSync  — debounced /cart.js fetch
       (single entry point for all sync operations)
    ───────────────────────────────────────────────── */
    var isGroupingItems = false;
    var syncDebounceTimer = null;
    /** Deferred retry when line rows are not in the DOM yet (max 3 per streak) */
    var bundleGroupRetryTimer = null;
    var bundleGroupMissRetries = 0;

    function fetchCartAndSync() {
      clearTimeout(syncDebounceTimer);
      syncDebounceTimer = setTimeout(function() {
        isGroupingItems = false; // always unlock before fetch so nothing stays stuck
        // cart-drawer-items.updateQuantity / renderContents replace .drawer__inner — must re-bind
        // #bbCartUpsell and [data-products] or bbBlock/tiers point at detached nodes.
        parseTierConfigFromDom();
        attachProductsObserver();
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) { syncVisibility(cart.items); })
          .catch(function() {
            isGroupingItems = false;
            clearCartUpdating();
          });
      }, 60);
    }
  
    /* ─────────────────────────────────────────────────
       EVENT LISTENERS (Dawn)
       CartItems.updateQuantity replaces entire .drawer__inner from cart/change —
       bundle markup is recreated; fetchCartAndSync must parseTierConfigFromDom()
       every time (see fetchCartAndSync).

       Sync entry points (debounced in fetchCartAndSync):
         A. cart-drawer:bundle-sync — from cart-drawer.js (open / refreshSections /
            renderContents) and cart.js after line-item quantity DOM update
         B. PUB_SUB_EVENTS.cartUpdate (source cart-items) — after cart/change paints
         C. MutationObserver on products container — childList / non–bundle-hiding attrs
    ───────────────────────────────────────────────── */

    document.addEventListener('cart-drawer:bundle-sync', function (e) {
      var cartItems = e.detail && e.detail.cartItems;
      if (cartItems) {
        // cart-drawer.js already fetched /cart.js and is passing the items directly.
        // Skip the 60 ms debounce and the duplicate network request so that
        // groupBundleItems runs synchronously here — before setTimeout(() => open()) fires.
        // The bundle group card is fully built before the drawer slides in.
        clearTimeout(syncDebounceTimer);
        parseTierConfigFromDom();
        attachProductsObserver();
        isGroupingItems = false;
        syncVisibility(cartItems);
      } else {
        fetchCartAndSync();
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'cart-items') {
          fetchCartAndSync();
        }
      });
    }

    attachProductsObserver();
  
    fetchCartAndSync();
  })();
  